import {useMemo, useRef, useEffect} from 'react';
import {useFunds} from '../../lib';
import {FundModel, FundWithMetaModel} from '../../model';
import {Select} from '@gravity-ui/uikit';
import styles from './SelectFunds.module.scss';

interface SelectFundsProps {
  value: string[];
  onUpdate: (value: string[]) => void;
  name?: string;
  size?: 's' | 'm' | 'l';
  placeholder?: string;
  error?: string | false;
  multiple?: boolean;
  isSaveSessionStorage?: boolean;
}

export const SelectFunds = ({
  value,
  onUpdate,
  name,
  size = 'm',
  placeholder = 'Фонд',
  error,
  multiple = false,
  isSaveSessionStorage = false,
}: SelectFundsProps) => {
  const {getInfiniteFunds} = useFunds({pageSize: 200});
  const fundSelectRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLButtonElement>(null);
  const {
    data: infiniteFundsData,
    isLoading: isFundsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = getInfiniteFunds;
  const fundOptions = useMemo(() => {
    return (
      infiniteFundsData?.pages.flatMap((page: FundWithMetaModel) =>
        page.items.map((fund: FundModel) => ({
          value: fund.id,
          content: fund.name,
        })),
      ) || []
    );
  }, [infiniteFundsData]);

  // Автоматически загружаем все страницы при наличии следующей страницы
  useEffect(() => {
    if (hasNextPage && !isFundsLoading && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFundsLoading, isFetchingNextPage, fetchNextPage]);

  // Обработчик открытия выпадающего списка
  const handleFundSelectOpen = (open: boolean) => {
    if (open) {
      // Добавляем обработчик скролла при открытии селекта
      setTimeout(() => {
        const popupElement = document.querySelector('.g-select-popup__content');
        if (popupElement) {
          fundSelectRef.current = popupElement as HTMLDivElement;
          popupElement.addEventListener('scroll', handleFundSelectScroll);
        }
      }, 100);
    } else {
      // Удаляем обработчик при закрытии
      if (fundSelectRef.current) {
        fundSelectRef.current.removeEventListener('scroll', handleFundSelectScroll);
        fundSelectRef.current = null;
      }
    }
  };

  // Обработчик скролла в выпадающем списке
  const handleFundSelectScroll = () => {
    if (!fundSelectRef.current) return;

    const {scrollTop, scrollHeight, clientHeight} = fundSelectRef.current;
    // Если пользователь прокрутил до конца списка и есть ещё данные
    if (scrollTop + clientHeight >= scrollHeight - 20 && hasNextPage && !isFundsLoading && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Обработчик обновления с сохранением в sessionStorage
  const handleUpdate = (newValue: string[]) => {
    // Вызываем обычное обновление
    onUpdate(newValue);

    // Если включено сохранение в sessionStorage
    if (isSaveSessionStorage) {
      // Находим объекты выбранных категорий с id и именем
      const selectedFunds = newValue
        .map((id) => {
          const option = fundOptions.find((opt) => opt.value === id);
          return option ? {id: option.value, name: option.content} : null;
        })
        .filter(Boolean);

      // Сохраняем в sessionStorage
      sessionStorage.setItem(`funds-${name || 'default'}`, JSON.stringify(selectedFunds));
    }
  };

  return (
    <Select
      className={styles.select}
      size={size}
      ref={selectRef}
      name={name}
      options={fundOptions}
      onUpdate={handleUpdate}
      value={value}
      onOpenChange={handleFundSelectOpen}
      loading={isFundsLoading}
      placeholder={placeholder}
      errorPlacement="inside"
      errorMessage={error}
      validationState={error ? 'invalid' : undefined}
      filterable
      filterPlaceholder="Поиск"
      multiple={multiple}
    />
  );
};
